import { apiClient } from "@/config/api";
import { ApiResponse } from "@/types/status";

// External data types
export interface ExternalCategory {
  _id: string;
  name: string;
  url: string;
}

export interface ExternalActor {
  _id: string;
  name: string;
  image_url?: string;
  description?: string;
  birthday?: string;
  height?: number;
  url?: string;
}

export interface ExternalDirector {
  _id: string;
  name: string;
  image_url?: string;
  description?: string;
  birthday?: string;
  height?: number;
  url?: string;
}

export interface ExternalCountry {
  _id: string;
  name: string;
  url: string;
}

export interface ExternalMovie {
  _id: string;
  name: string;
  url?: string;
  description?: string;
  year?: number;
  status?: string;
  type?: string;
  current_episode?: number;
  total_episode?: number;
  category?: Array<{
    _id: string;
    name: string;
    url: string;
  }> | string[] | {
    _id: string;
    name: string;
    url: string;
  };
  actors?: Array<{
    _id: string;
    name: string;
    url?: string;
  }>;
  directors?: Array<{
    _id: string;
    name: string;
    url?: string;
  }>;
  duration?: number;
  quality?: string;
  trailer?: string;
  tags?: string[];
  release_date?: string;
  origin_name?: string;
}

export interface ExternalAuthor {
  _id: string;
  name: string;
  username: string;
  email: string;
}

const EXTERNAL_ENDPOINTS = {
  CATEGORIES: "/external/categories",
  MOVIES: "/external/movies",
  ACTORS: "/external/actors",
  DIRECTORS: "/external/directors",
  COUNTRIES: "/external/countries",
  MOVIE_BY_ID: (id: string) => `/external/movies/${id}`,
  ACTOR_BY_ID: (id: string) => `/external/actors/${id}`,
  DIRECTOR_BY_ID: (id: string) => `/external/directors/${id}`,
  CATEGORY_BY_ID: (id: string) => `/external/categories/${id}`,
} as const;

export const externalService = {
  // Get all categories
  getAllCategories: async (): Promise<ApiResponse<ExternalCategory[]>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.CATEGORIES);
  },

  // Get all movies
  getAllMovies: async (): Promise<ApiResponse<ExternalMovie[]>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.MOVIES);
  },

  // Get all actors
  getAllActors: async (): Promise<ApiResponse<ExternalActor[]>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.ACTORS);
  },

  // Get all directors
  getAllDirectors: async (): Promise<ApiResponse<ExternalDirector[]>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.DIRECTORS);
  },

  // Get all countries
  getAllCountries: async (): Promise<ApiResponse<ExternalCountry[]>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.COUNTRIES);
  },

  // Get movie by ID
  getMovieById: async (id: string): Promise<ApiResponse<ExternalMovie>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.MOVIE_BY_ID(id));
  },

  // Get actor by ID
  getActorById: async (id: string): Promise<ApiResponse<ExternalActor>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.ACTOR_BY_ID(id));
  },

  // Get director by ID
  getDirectorById: async (
    id: string
  ): Promise<ApiResponse<ExternalDirector>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.DIRECTOR_BY_ID(id));
  },

  // Get category by ID
  getCategoryById: async (
    id: string
  ): Promise<ApiResponse<ExternalCategory>> => {
    return await apiClient.get(EXTERNAL_ENDPOINTS.CATEGORY_BY_ID(id));
  },
};
